import { Injectable, signal, computed } from '@angular/core';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  BizFrontendApiService,
  BizFrontendSettingService,
} from '@interserve/angular';
import {
  ScreenMessageItem,
  ScreenMessageByViewCd,
} from '../config/screen-message.types';

/** getShearedUserSettingsForSettingCd の target（設定の取得先） */
const SCREEN_MESSAGE_TARGET = 'inventory-report';
/** getShearedUserSettingsForSettingCd の settingCd */
const SCREEN_MESSAGE_SETTING_CD = 'screenMessage';

/**
 * API レスポンスを viewCd 別メッセージに正規化
 * 形式1: { screenMessage: { messages: [{ viewCd, message, enable }] } }
 * 形式2: { enable: boolean, messages: [{ viewCd, message, enable }] }
 * 形式3: Record<viewCd, ScreenMessageItem[]>
 */
function normalizeToByViewCd(raw: unknown): ScreenMessageByViewCd {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as Record<string, unknown>;

  // messages 配列を viewCd 単位へ正規化（message/text 両対応）
  const normalizeMessages = (
    messages: unknown[],
    globalEnabled: boolean
  ): ScreenMessageByViewCd => {
    const byView: ScreenMessageByViewCd = {};
    for (const item of messages) {
      if (!item || typeof item !== 'object') continue;
      const m = item as {
        viewCd?: string;
        message?: string;
        text?: string;
        enable?: boolean;
        enabled?: boolean;
        severity?: ScreenMessageItem['severity'];
      };
      const viewCd = m.viewCd;
      const text = typeof m.message === 'string' ? m.message : m.text;
      if (!viewCd || typeof text !== 'string') continue;
      if (!byView[viewCd]) byView[viewCd] = [];
      byView[viewCd].push({
        text,
        severity: m.severity,
        enabled: globalEnabled && m.enable !== false && m.enabled !== false,
      });
    }
    return byView;
  };

  const screenMessage = obj['screenMessage'];
  if (screenMessage && typeof screenMessage === 'object') {
    const screenMessageObj = screenMessage as Record<string, unknown>;
    const sm = screenMessageObj['messages'];
    if (Array.isArray(sm)) {
      const globalEnabled = screenMessageObj['enable'] !== false;
      return normalizeMessages(sm, globalEnabled);
    }
  }

  // 設定CDが screenMessage 本体の場合（{ enable, messages }）
  const directMessages = obj['messages'];
  if (Array.isArray(directMessages)) {
    const globalEnabled = obj['enable'] !== false;
    return normalizeMessages(directMessages, globalEnabled);
  }

  const byView: ScreenMessageByViewCd = {};
  for (const [viewCd, arr] of Object.entries(obj)) {
    if (!Array.isArray(arr)) continue;
    byView[viewCd] = arr
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const m = item as {
          text?: string;
          message?: string;
          enabled?: boolean;
          enable?: boolean;
          severity?: ScreenMessageItem['severity'];
        };
        const text = typeof m.text === 'string' ? m.text : m.message;
        if (typeof text !== 'string') return null;
        return {
          text,
          severity: m.severity,
          enabled: m.enabled ?? m.enable,
        } as ScreenMessageItem;
      })
      .filter((item): item is ScreenMessageItem => item !== null);
  }
  return byView;
}

/**
 * 画面メッセージ（screenMessage）取得サービス
 * ライブラリの getShearedUserSettingsForSettingCd で取得。API 未配信・エラー時は表示しない。
 */
@Injectable({
  providedIn: 'root',
})
export class ScreenMessageService {
  /** API から取得した viewCd 別メッセージ（API 未配信時は空） */
  private messagesByViewCd = signal<ScreenMessageByViewCd>({});

  readonly isConfigLoading = signal<boolean>(false);

  /** 現在ルートから解決した viewCd */
  currentViewCd = signal<string | null>(null);

  /** 表示するメッセージ一覧（現在の viewCd に対応、enabled のみ。API 未配信なら空） */
  readonly currentMessages = computed<ScreenMessageItem[]>(() => {
    const viewCd = this.currentViewCd();
    const byView = this.messagesByViewCd();
    if (!viewCd || !byView[viewCd]) return [];
    return (byView[viewCd] || []).filter((m) => m.enabled !== false);
  });

  /** 1件目（バナー用）。複数件時は残件カウント用に長さを利用 */
  readonly firstMessage = computed<ScreenMessageItem | null>(() => {
    const list = this.currentMessages();
    return list.length > 0 ? list[0] : null;
  });

  /** 残件数（2件目以降） */
  readonly restCount = computed<number>(() => {
    const list = this.currentMessages();
    return Math.max(0, list.length - 1);
  });

  constructor(
    private router: Router,
    private bizFrontendApiService: BizFrontendApiService,
    private bizFrontendSettingService: BizFrontendSettingService
  ) {
    this.loadScreenMessages().subscribe();
    this.router.events.subscribe(() => this.resolveViewCd());
    this.resolveViewCd();
  }

  /**
   * 現在のルートから viewCd を解決（data.viewCd または workMode パラメータ）
   */
  private resolveViewCd(): void {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let viewCd: string | null = null;
    let params: Record<string, string> = {};

    while (route) {
      if (route.data['viewCd']) viewCd = route.data['viewCd'];
      if (route.params) params = { ...params, ...route.params };
      route = route.firstChild ?? null;
    }

    const resolved = viewCd ?? params['workMode'] ?? null;
    this.currentViewCd.set(resolved);
  }

  /**
   * ライブラリの getShearedUserSettingsForSettingCd で画面メッセージ設定を取得。
   * API 未配信・エラー時は表示しない。
   */
  loadScreenMessages(): Observable<boolean> {
    this.isConfigLoading.set(true);

    return this.bizFrontendApiService
      .getShearedUserSettingsForSettingCd(
        SCREEN_MESSAGE_SETTING_CD,
        SCREEN_MESSAGE_TARGET
      )
      .pipe(
        map((response) => {
          this.isConfigLoading.set(false);
          const payload =
            response && typeof response === 'object' && 'data' in response
              ? (response as { resultCode?: number; data?: unknown }).data
              : response;
          const byView = normalizeToByViewCd(payload);
          this.messagesByViewCd.set(byView);
          return Object.keys(byView).length > 0;
        }),
        catchError(() => {
          this.isConfigLoading.set(false);
          this.messagesByViewCd.set({});
          return of(false);
        })
      );
  }
}
